'use client'

import { Box, Divider, Stack } from '@mantine/core'

import type { PredictionNavNode } from '@/services/prediction/types'

import { NavTreeAccordionProvider, NavTreeItem } from '../NavTree/items'
import itemStyles from '../NavTree/items.module.css'
import { activeChildId, isAllNode, isFolderAllActive, sortByOrdering } from '../NavTree/utils'
import styles from './styles.module.css'

interface NavTreeSidebarProps {
  /** The currently selected L1 root; its subtree is what the sidebar renders. */
  rootNode: PredictionNavNode
  /** Selected node's `path` — drives the row highlight/expand state. */
  selectedPath: string
  onSelect: (node: PredictionNavNode) => void
}

/**
 * Desktop category sidebar — one L1's subtree, sticky, self-scrolling.
 *
 * Port of cdc-web's NavTreeSidebar with adaptations:
 * - Selection is local state (no pathname / optimistic URL): `selectedPath`
 *   plays the role cdc-web's de-prefixed pathname did.
 * - Root node is handed in from the section (which also owns the nav-tree
 *   query for the L1 chips) instead of being derived from the URL here.
 * - Width is 263px per the section layout (cdc-web used 300px on the full
 *   category page).
 *
 * Top-level children are split visually by node_type: destinations render
 * first (All + Crypto's duration filters all fall in here, ordered by BE's
 * `ordering`), then a divider, then folders (token / sport / instrument
 * groups). Categories that only have a single destination skip the divider.
 */
export default function NavTreeSidebar({
  rootNode,
  selectedPath,
  onSelect,
}: Readonly<NavTreeSidebarProps>) {
  const visibleChildren = sortByOrdering(
    (rootNode.children ?? []).filter(c => c.node_type !== 'scope'),
  )
  if (visibleChildren.length === 0) return null

  const destinationChildren = visibleChildren.filter(c => c.node_type === 'destination')
  const folderChildren = visibleChildren.filter(c => c.node_type === 'folder')
  // A divider is only drawn when the destination group is a real cluster.
  // When it isn't (a lone "All" above the folders), the two `<Stack>`s would
  // butt together with 0 gap, unlike the `3xs` gap between rows *inside*
  // each stack — add a matching `3xs` top margin to the folder stack in that
  // case (OG-10143).
  const showDivider = destinationChildren.length > 1 && folderChildren.length > 0

  // 选中 L1 根（selectedPath === rootNode.path）时左树要有默认高亮项。嵌套
  // folder（Soccer 等）在 FolderItem 内部已处理（它能看到自己的 All child），
  // 但根自身的 All 行是 sidebar 顶层渲染的，没有父 folder 传 forceActive——
  // 所以由 sidebar 用同一套 isFolderAllActive 判定补上（选中根 / 根的 All /
  // 根的直接 scope 三种 All context 都会点亮根 All 行）。
  const rootAllActive = isFolderAllActive(rootNode, selectedPath)

  return (
    // The sidebar is its own scroll container (sticky + max-height +
    // overflow auto, all on the same element — see styles.module.css for
    // the rationale).
    <Box component="aside" className={styles.sidebar} aria-label={rootNode.display_name}>
      {/* One accordion scope across both lists: opening a folder collapses any
          other open folder, even though destinations and folders render as two
          separate `<Stack>`s (OG-7173 #3b). Seeded from all visible children so
          the folder on the selected path starts expanded. */}
      <NavTreeAccordionProvider seedId={activeChildId(visibleChildren, selectedPath)}>
        {destinationChildren.length > 0 && (
          <Stack component="ul" gap="3xs" className={itemStyles.list}>
            {destinationChildren.map(child => (
              <NavTreeItem
                key={child.id}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
                forceActive={rootAllActive && isAllNode(child)}
              />
            ))}
          </Stack>
        )}
        {/* Only divide top-level destinations from folders when the destination
            group is itself a logical cluster (e.g. Crypto's duration filters)
            — for categories with just a single "All" a divider would imply a
            section break that doesn't exist. */}
        {showDivider && <Divider my="xs" />}
        {folderChildren.length > 0 && (
          <Stack
            component="ul"
            gap="3xs"
            className={itemStyles.list}
            mt={!showDivider && destinationChildren.length > 0 ? '3xs' : undefined}
          >
            {folderChildren.map(child => (
              <NavTreeItem
                key={child.id}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
              />
            ))}
          </Stack>
        )}
      </NavTreeAccordionProvider>
    </Box>
  )
}
